/* eslint-disable import/prefer-default-export */
import { Options } from 'ky';
import qs from 'qs';
import config from 'config';
import { omit } from 'ramda';
import { useLocalStorageState } from 'ahooks';
import { AccountPersistInfo } from 'Internal.CommonAPI';
import { uploadImage } from 'Inventory/api/products';
import * as N from 'Common/notifications';
import {
  downloadFile,
  getCustomFiltersParams,
  getSortParam,
  requestUpload,
} from 'utils/api';
import { normalizeGiftcard, normalizeProduct } from 'Inventory/schemas';
import {
  CategoriesTableParams,
  GiftcardImports,
  Product,
  ProductListNormalizedRecord,
  ProductImports,
  SaveCategoryPayload,
  SaveCategoryResponse,
  UploadImagePayload,
  ShopActions,
  CategoriesListResponse,
  ImportFile,
  UpdateAveragePurchasePricePayload,
  UpdatePurchasePriceResponse,
  ProductType,
  PreCodedCard,
} from './types';
import { useAuthenticatedApiClient } from '../../base';

export type ProductsType = {
  results: Array<Product> | Array<ProductListNormalizedRecord>;
  count: number;
};

const apiVersion13 = {
  'x-api-version': '13',
};

const customJsonHeader = {
  'Content-Type': 'application/json',
};

const productClientOpts: Options = {
  prefixUrl: `${config.CORE_URL}/api/`,
};

const CATEGORIES_URL = `${config.CORE_URL}/api/categories`;
const GIFTCARDS_URL = `v2/giftcards/`;

export const useInventoryApi = () => {
  const authenticatedApiClient = useAuthenticatedApiClient();
  const [account] = useLocalStorageState<AccountPersistInfo>('account');
  const inventoryApiClient = authenticatedApiClient.extend(productClientOpts);

  return {
    fetchCategories: async (tableParams: CategoriesTableParams) => {
      const params = {
        page: tableParams?.pagination?.current,
        limit: tableParams?.pagination?.pageSize,
        sort: getSortParam(tableParams?.sorter!),
        q: tableParams?.search ?? undefined,
      };

      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });

      return authenticatedApiClient
        .get(`${config.CORE_URL}/api/categories/${paramsQs}`, {
          headers: {
            ...apiVersion13,
            ...customJsonHeader,
          },
        })
        .json<CategoriesListResponse>();
    },

    saveCategory: (payload: {
      data: SaveCategoryPayload;
      createdForProduct?: boolean;
    }) => {
      if (payload.data.id) {
        return authenticatedApiClient
          .put(`${CATEGORIES_URL}/${payload.data.id}/`, {
            headers: { ...apiVersion13, ...customJsonHeader },
            body: JSON.stringify(payload.data),
          })
          .json();
      }
      return authenticatedApiClient
        .post(`${CATEGORIES_URL}/`, {
          headers: { ...apiVersion13, ...customJsonHeader },
          body: JSON.stringify(payload.data),
        })
        .json<SaveCategoryResponse>();
    },

    deleteCategory: (id: number) =>
      authenticatedApiClient.delete(`${CATEGORIES_URL}/${id}/`, {
        headers: apiVersion13,
      }),
    manageImage: async (payload: UploadImagePayload) => {
      try {
        const result = await uploadImage(payload, account?.userToken);
        N.pushUploadImageSuccess(result);
        return result;
      } catch (error) {
        N.pushUploadImageFail();
        return null;
      }
    },

    fetchProducts: async (keyParams: any) => {
      const {
        sorter,
        filters,
        pagination,
        search,
        customFilters = {},
        skip_customFilters,
        isServicesQuery = false,
        ...rest
      } = keyParams;
      const isMainRequest = filters.parent__isnull;
      // eslint-disable-next-line no-unused-vars ,  @typescript-eslint/no-unused-vars
      const { gcv2, tableParams, ...others } = rest;
      const customFiltersQueries = getCustomFiltersParams(customFilters);

      const getPaginationParams = () => {
        if (isServicesQuery || filters.is_giftcard) {
          return {
            page: pagination.current,
            limit: pagination.pageSize,
          };
        }
        return {
          page: isMainRequest ? pagination.current : 1,
          limit: isMainRequest ? pagination.pageSize : 250,
          parent__isnull: isMainRequest,
        };
      };

      const paginationParams = getPaginationParams();

      const params = {
        ...(search && { name__contains: search }),
        ...paginationParams,
        ordering: getSortParam(sorter),
        ...(!skip_customFilters && customFiltersQueries),
        ...others,
      };

      const querystring = qs.stringify(
        {
          ...params,
          ...(!filters.is_giftcard && filters),
        },
        {
          addQueryPrefix: true,
          skipNulls: true,
          arrayFormat: 'comma',
        }
      );
      const fullUrl = [
        filters.is_giftcard ? `${GIFTCARDS_URL}` : `products/dashboard/`,
        querystring,
      ].join('');
      return inventoryApiClient
        .get(fullUrl, {
          headers: { ...customJsonHeader },
          ...(!filters.is_giftcard && { timeout: 40000 }),
        })
        .json<ProductsType>();
    },
    fetchProductDetails: async (id: number) => {
      const result = await inventoryApiClient
        .get(`products/${id}/`, {
          headers: apiVersion13,
        })
        .json<Product>();
      return normalizeProduct(result);
    },
    fetchGiftCardDetails: async (id: number) => {
      const result = await inventoryApiClient
        .get(`${GIFTCARDS_URL}${id}/`, {
          headers: apiVersion13,
        })
        .json<Product>();
      return normalizeGiftcard(result);
    },
    fetchAttributes: async (lang: string) => {
      const url = `${config.CORE_URL}/api/public/info/attributes/?language=${lang}`;
      return authenticatedApiClient
        .get(url, {
          headers: apiVersion13,
        })
        .then((result) => result.json());
    },
    uploadProductsImport: async (token: string, formData: ImportFile) => {
      const url = `${config.CORE_URL}/api/products/import/`;
      await requestUpload({
        url,
        userToken: token,
        ...apiVersion13,
        requestData: formData,
      });
    },
    getProductsImports: () =>
      inventoryApiClient
        .get(`products/import/`, {
          headers: { ...apiVersion13, ...customJsonHeader },
        })
        .json<ProductImports>(),
    generateProductsTemplate: async (lang: string) => {
      const response = inventoryApiClient.get(
        `products/import/template.xlsx?language=${lang.toLowerCase()}`,
        {
          headers: apiVersion13,
        }
      );
      const template = await response.blob();
      downloadFile(template, 'products-template', 'xlsx');
    },
    saveProduct: (product: any) =>
      product.id
        ? inventoryApiClient
            .put(`products/${product.id}/`, {
              headers: { ...apiVersion13, ...customJsonHeader },
              body: JSON.stringify(product),
              timeout: 60000,
            })
            .json<Product>()
        : inventoryApiClient
            .post(`products/`, {
              headers: { ...apiVersion13, ...customJsonHeader },
              body: JSON.stringify(omit(['id'], product)),
              timeout: 60000,
            })
            .json<Product>(),
    saveGiftcard: (giftcard: any) =>
      giftcard.id
        ? inventoryApiClient
            .put(`${GIFTCARDS_URL}${giftcard.id}/`, {
              headers: { ...apiVersion13, ...customJsonHeader },
              body: JSON.stringify(giftcard),
            })
            .json()
        : inventoryApiClient
            .post(`${GIFTCARDS_URL}`, {
              headers: { ...apiVersion13, ...customJsonHeader },
              body: JSON.stringify(omit(['id'], giftcard)),
            })
            .json(),
    getGiftCard: (giftcardId: number) =>
      inventoryApiClient.get(`${GIFTCARDS_URL}${giftcardId}/`, {
        headers: { ...apiVersion13, ...customJsonHeader },
      }),
    generateGiftcardsTemplate: async (lang: string) => {
      const response = inventoryApiClient.get(
        `${GIFTCARDS_URL}import/template.xlsx?language=${lang.toLowerCase()}`,
        {
          headers: apiVersion13,
        }
      );
      const template = await response.blob();
      downloadFile(template, 'giftcards-template', 'xlsx');
    },
    uploadGiftcardsImport: async (token: string, formData: ImportFile) => {
      const url = `${config.CORE_URL}/api/${GIFTCARDS_URL}import`;
      await requestUpload({
        url,
        userToken: token,
        ...apiVersion13,
        requestData: formData,
      });
    },
    getGiftcardsImports: () =>
      inventoryApiClient
        .get(`${GIFTCARDS_URL}import`, {
          headers: { ...apiVersion13, ...customJsonHeader },
        })
        .json<GiftcardImports>(),
    revertGiftcardImport: (importId: number) =>
      inventoryApiClient.post(`${GIFTCARDS_URL}import/${importId}/revert`, {
        headers: { ...apiVersion13, ...customJsonHeader },
      }),
    getRedeemCodes: (giftcardId: number) =>
      inventoryApiClient
        .get(`${GIFTCARDS_URL}${giftcardId}/pre_coded/`, {
          headers: { ...apiVersion13, ...customJsonHeader },
        })
        .json<Array<PreCodedCard>>(),
    saveRedeemCodes: (giftcardId: number, rclist: any) =>
      inventoryApiClient.post(`${GIFTCARDS_URL}${giftcardId}/pre_coded/`, {
        body: JSON.stringify(rclist),
        headers: { ...apiVersion13, ...customJsonHeader },
      }),
    deleteRedeemCode: (giftcardId: number, cardId: number) =>
      inventoryApiClient.delete(
        `${GIFTCARDS_URL}${giftcardId}/pre_coded/${cardId}/`,
        {
          headers: { ...apiVersion13, ...customJsonHeader },
          body: JSON.stringify(cardId),
        }
      ),
    deleteGiftCard: (giftcardId: number) =>
      inventoryApiClient.delete(`${GIFTCARDS_URL}${giftcardId}/`, {
        headers: { ...apiVersion13, ...customJsonHeader },
      }),
    deleteProduct: (productId: number) =>
      inventoryApiClient.delete(`products/${productId}/`, {
        headers: { ...apiVersion13, ...customJsonHeader },
      }),
    triggerBulkAction: ({
      values: { action, itemsIds },
      itemType,
    }: {
      values: { action: string; itemsIds: Product[] };
      itemType: ProductType;
    }) => {
      const items = itemsIds.map((item) => ({
        id: itemType === ProductType.Service ? item.id : item,
      }));
      const toBeDeletedList = itemsIds.map((item: Product) => item.id);
      if (action === 'DELETE') {
        return inventoryApiClient.delete('products/', {
          body: JSON.stringify(
            itemType === ProductType.Service ? toBeDeletedList : itemsIds
          ),
          headers: { ...apiVersion13, ...customJsonHeader },
        });
      }
      return inventoryApiClient.post('products/shop/publish/', {
        body: JSON.stringify(items),
        headers: { ...apiVersion13, ...customJsonHeader },
      });
    },
    updateProductAvgPurchasePrice: async (
      id: number,
      values: UpdateAveragePurchasePricePayload
    ) => {
      const response = await inventoryApiClient.post(
        `products/${id}/reset_purchase_price/`,
        {
          body: JSON.stringify(values),
          headers: { ...apiVersion13, ...customJsonHeader },
        }
      );
      if (response.status !== 200) {
        const data: any = response.json();
        throw new Error(data.error);
      }
      return {
        id,
        purchase_price: values.purchase_price,
      } as UpdatePurchasePriceResponse;
    },
    adjustStock: async (id: number, values: any) => {
      const response = await inventoryApiClient.put(
        `products/${id}/adjust_inventory/`,
        {
          body: JSON.stringify(values),
          headers: { ...apiVersion13, ...customJsonHeader },
        }
      );
      if (response.status !== 200) {
        const data: any = response.json();
        throw new Error(data.error);
      }
      return response.json();
    },
    pushToShop: async (id: number, action: any) => {
      const url = `products/${id}/shop/`;
      const method = action === ShopActions.Unpublish ? 'DELETE' : 'POST';
      if (method === 'DELETE') {
        return inventoryApiClient.delete(url, {
          headers: { ...apiVersion13, ...customJsonHeader },
        });
      }
      return inventoryApiClient
        .post(url, {
          headers: { ...apiVersion13, ...customJsonHeader },
          body: null,
        })
        .json();
    },
  };
};
